import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCancelInvoice, useDeleteInvoice } from '@/hooks/useInvoices';
import { invoiceDeleteIsRisky } from '@/types/invoice';
import { Loader2 } from 'lucide-react';

export type InvoiceConfirmAction = 'cancel' | 'delete';

interface InvoiceActionConfirmDialogProps {
  action: InvoiceConfirmAction | null;
  invoice: { id: string; invoice_number: string; status: string; provider_invoice_id?: string | null; amount_paid?: number | null } | null;
  onOpenChange: (open: boolean) => void;
  /** Called after the mutation succeeds (e.g. close the parent sheet on delete). */
  onDone?: (action: InvoiceConfirmAction) => void;
}

/**
 * Office-side "Cancel invoice" / "Delete invoice" confirmation.
 * Cancel also voids the Square invoice when one was published, so the pay
 * link stops taking money. Delete works on any invoice so mistakes can be
 * cleaned up; when money is recorded against it the copy says what is lost and
 * only users who can see company financials are offered the action.
 */
export function InvoiceActionConfirmDialog({ action, invoice, onOpenChange, onDone }: InvoiceActionConfirmDialogProps) {
  const cancelInvoice = useCancelInvoice();
  const deleteInvoice = useDeleteInvoice();
  const busy = cancelInvoice.isPending || deleteInvoice.isPending;
  const open = !!action && !!invoice;
  const hasMoneyOnIt = !!invoice && invoiceDeleteIsRisky(invoice);

  const handleConfirm = async () => {
    if (!action || !invoice) return;
    try {
      if (action === 'cancel') {
        await cancelInvoice.mutateAsync(invoice.id);
      } else {
        await deleteInvoice.mutateAsync(invoice.id);
      }
      onOpenChange(false);
      onDone?.(action);
    } catch {
      // Toast raised by the hook; keep the dialog open so the user can retry or back out.
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {action === 'delete' ? 'Delete invoice' : 'Cancel invoice'}
            {invoice ? ` ${invoice.invoice_number}` : ''}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {action === 'delete'
              ? hasMoneyOnIt
                ? 'This invoice has payments recorded against it. Deleting it removes the invoice for good; the payments stay on the job but no longer point at an invoice, so invoice reporting changes. This cannot be undone.'
                : 'This permanently removes the invoice and cannot be undone. Use this to clean up a mistake.'
              : invoice?.provider_invoice_id
                ? 'The Square invoice is cancelled as well, so the customer’s pay link stops working. It stays on record; you can delete it afterwards if it was a mistake.'
                : 'The invoice is marked cancelled and can no longer be sent for payment. It stays on record; you can delete it afterwards if it was a mistake.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Keep invoice</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            disabled={busy}
            className={action === 'delete' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {action === 'delete' ? 'Delete permanently' : 'Cancel invoice'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
